import React, { useRef, useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import {
  motion, useInView, useScroll, useTransform, useSpring,
  useMotionValue, AnimatePresence
} from 'framer-motion';
import {
  ArrowRight, Check, X, Play,
  Globe, Smartphone, CreditCard, Package, BarChart3, Users,
  Crown, Upload, Rocket, Gift, Sparkles, Clock,
  Instagram, MessageCircle, Eye, TrendingUp, ShoppingBag,
  Share2, Palette, Image, MousePointerClick, Zap, IndianRupee,
  Truck, Star
} from 'lucide-react';
import UniversalNavbar from '../components/UniversalNavbar';
import VFooter from '../components/VFooter';
import COLORS from '../utils/colors';

/* CloudFront base for all static assets */
const CDN = 'https://d3qvi06vol9hrb.cloudfront.net/static/media';

/* Template preview screenshots */
const tplMinimal = `${CDN}/template1-preview.a716e2154d93a137787b.png`;
const tplStreet = `${CDN}/template2-preview.6a18dd7646218f101ebd.png`;
const tplLuxury = `${CDN}/template3-preview.b72361b87739e0acd742.png`;
const tplVibrant = `${CDN}/template4-preview.759895de197e8d2f81bd.png`;
const tplTrendy = `${CDN}/template5-preview.ed38ed642e361a64ab78.png`;
const tplEthnic = `${CDN}/template6-preview.09a96c81d0f19b0138f6.png`;

/* Fashion stock photos */
const fashionRunway = `${CDN}/indian-fashion-runway.ed37650b6271a854f9bf.jpg`;
const fashionSaree = `${CDN}/indian-saree-model.6a7ad909b99be6533d46.jpg`;
const fashionStudio = `${CDN}/indian-designer-studio.f74d978bc0e20e8e986f.jpg`;
const fashionBoutique = `${CDN}/fashion-boutique.f89c2c56cfc6894dbdc3.jpg`;

/* ─── Typography ─── */
const SERIF = "'Playfair Display', Georgia, serif";
const SANS = "'Manrope', sans-serif";

/* ─── Easing ─── */
const EASE = [0.22, 1, 0.36, 1];

/* ════════════════════════════════════════════════════════════════
   ANIMATION COMPONENTS — the engine that makes the page alive
   ════════════════════════════════════════════════════════════════ */

/* 1. Scroll Progress Bar — fixed at top */
const ScrollProgress = () => {
  const { scrollYProgress } = useScroll();
  const scaleX = useSpring(scrollYProgress, { stiffness: 100, damping: 30, restDelta: 0.001 });
  return (
    <motion.div
      className="fixed top-0 left-0 right-0 h-[3px] origin-left"
      style={{ scaleX, backgroundColor: COLORS.AUTUMN, zIndex: 9999 }}
    />
  );
};

/* 2. Reveal — scroll-triggered with direction variety */
const Reveal = ({ children, delay = 0, className = '', direction = 'up', distance = 60 }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-80px' });
  const dirs = {
    up: { y: distance }, down: { y: -distance },
    left: { x: -distance }, right: { x: distance },
    scale: { scale: 0.85, y: 30 }, none: {}
  };
  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, ...dirs[direction] }}
      animate={inView ? { opacity: 1, x: 0, y: 0, scale: 1 } : {}}
      transition={{ duration: 0.8, delay, ease: EASE }}
      className={className}
    >
      {children}
    </motion.div>
  );
};

/* 3. Stagger container + child with direction */
const StaggerWrap = ({ children, className = '', stagger = 0.1, margin = '-60px' }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin });
  return (
    <motion.div
      ref={ref} initial="hidden"
      animate={inView ? 'visible' : 'hidden'}
      variants={{ hidden: {}, visible: { transition: { staggerChildren: stagger } } }}
      className={className}
    >
      {children}
    </motion.div>
  );
};

const StaggerChild = ({ children, className = '', direction = 'up' }) => {
  const dirs = {
    up: { y: 50 }, down: { y: -50 },
    left: { x: -50 }, right: { x: 50 },
    scale: { scale: 0.85 }
  };
  return (
    <motion.div
      variants={{
        hidden: { opacity: 0, ...dirs[direction] },
        visible: { opacity: 1, x: 0, y: 0, scale: 1, transition: { duration: 0.7, ease: EASE } }
      }}
      className={className}
    >
      {children}
    </motion.div>
  );
};

/* 4. Infinite Marquee — horizontal scrolling */
const Marquee = ({ children, speed = 35 }) => (
  <div className="overflow-hidden">
    <motion.div
      className="flex"
      animate={{ x: ['0%', '-50%'] }}
      transition={{ duration: speed, repeat: Infinity, ease: 'linear' }}
    >
      <div className="flex shrink-0">{children}</div>
      <div className="flex shrink-0">{children}</div>
    </motion.div>
  </div>
);

/* 5. Counter — animates numbers on scroll */
const CountUp = ({ end, prefix = '', duration = 1.5 }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true });
  const [val, setVal] = useState(0);
  useEffect(() => {
    if (!inView || end === 0) { setVal(end); return; }
    let frame = 0;
    const frames = Math.round(duration * 60);
    const counter = setInterval(() => {
      frame++;
      const progress = 1 - Math.pow(1 - frame / frames, 3);
      setVal(Math.round(progress * end));
      if (frame >= frames) { setVal(end); clearInterval(counter); }
    }, 1000 / 60);
    return () => clearInterval(counter);
  }, [inView, end, duration]);
  return <span ref={ref}>{prefix}{val.toLocaleString('en-IN')}</span>;
};

/* 6. 3D Tilt Card — perspective on hover */
const TiltCard = ({ children, className = '' }) => {
  const ref = useRef(null);
  const xVal = useMotionValue(0);
  const yVal = useMotionValue(0);
  const rotateX = useSpring(yVal, { stiffness: 200, damping: 20 });
  const rotateY = useSpring(xVal, { stiffness: 200, damping: 20 });
  return (
    <div style={{ perspective: 1000 }}>
      <motion.div
        ref={ref}
        onMouseMove={(e) => {
          const rect = ref.current?.getBoundingClientRect();
          if (!rect) return;
          xVal.set(((e.clientX - rect.left) / rect.width - 0.5) * 10);
          yVal.set(((e.clientY - rect.top) / rect.height - 0.5) * -10);
        }}
        onMouseLeave={() => { xVal.set(0); yVal.set(0); }}
        style={{ rotateX, rotateY }}
        className={className}
      >
        {children}
      </motion.div>
    </div>
  );
};

/* 7. Magnetic Wrap — button follows cursor */
const MagneticWrap = ({ children, className = '', intensity = 0.15 }) => {
  const ref = useRef(null);
  const xVal = useMotionValue(0);
  const yVal = useMotionValue(0);
  const x = useSpring(xVal, { stiffness: 150, damping: 15 });
  const y = useSpring(yVal, { stiffness: 150, damping: 15 });
  return (
    <motion.div
      ref={ref}
      onMouseMove={(e) => {
        const rect = ref.current?.getBoundingClientRect();
        if (!rect) return;
        xVal.set((e.clientX - rect.left - rect.width / 2) * intensity);
        yVal.set((e.clientY - rect.top - rect.height / 2) * intensity);
      }}
      onMouseLeave={() => { xVal.set(0); yVal.set(0); }}
      style={{ x, y }}
      className={`inline-block ${className}`}
    >
      {children}
    </motion.div>
  );
};

/* 8. Section Heading — Playfair Display */
const Heading = ({ children, light = false, className = '' }) => (
  <h2
    className={`text-[2.25rem] sm:text-[3rem] lg:text-[3.75rem] ${className}`}
    style={{
      fontFamily: SERIF, fontWeight: 400, lineHeight: 1.1,
      letterSpacing: '-0.02em', color: light ? '#fff' : COLORS.TEXT_DARK,
    }}
  >
    {children}
  </h2>
);

/* ════════════════════════════════════════════════════════════════
   LANDING PAGE
   ════════════════════════════════════════════════════════════════ */
const LandingPage = () => {
  const [showVideoModal, setShowVideoModal] = useState(false);
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ['start start', 'end start'] });
  const heroY = useTransform(scrollYProgress, [0, 1], ['0%', '25%']);
  const heroOpacity = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  const socialProofItems = [
    { icon: Sparkles, text: '20+ Premium Templates' },
    { icon: Clock, text: '5-Minute Setup' },
    { icon: Globe, text: 'Your Own .vasstr.com' },
    { icon: CreditCard, text: 'Razorpay Payments' },
  ];

  return (
    <div className="min-h-screen" style={{ fontFamily: SANS }}>
      <ScrollProgress />
      <UniversalNavbar />

      {/* ══════════════════════════════════════════════
          HERO — MIDNIGHT with animated background
      ══════════════════════════════════════════════ */}
      <section
        ref={heroRef}
        className="relative min-h-screen flex items-center pt-20 pb-16 px-4 sm:px-6 lg:px-8 overflow-hidden"
        style={{ backgroundColor: COLORS.LINEN }}
      >
        {/* Animated floating orbs */}
        <motion.div
          className="absolute w-[600px] h-[600px] rounded-full blur-[150px]"
          style={{ backgroundColor: `${COLORS.AUTUMN}08`, top: '15%', right: '15%' }}
          animate={{ x: [0, 40, -30, 0], y: [0, -50, 25, 0], scale: [1, 1.15, 0.9, 1] }}
          transition={{ duration: 20, repeat: Infinity, ease: 'easeInOut' }}
        />
        <motion.div
          className="absolute w-[400px] h-[400px] rounded-full blur-[120px]"
          style={{ backgroundColor: `${COLORS.MIDNIGHT}06`, bottom: '10%', left: '5%' }}
          animate={{ x: [0, -35, 25, 0], y: [0, 30, -40, 0] }}
          transition={{ duration: 25, repeat: Infinity, ease: 'easeInOut' }}
        />
        {/* Subtle dot grid */}
        <div className="absolute inset-0 opacity-[0.03]" style={{
          backgroundImage: `radial-gradient(circle at 1px 1px, ${COLORS.MIDNIGHT}15 1px, transparent 0)`,
          backgroundSize: '40px 40px'
        }} />

        <motion.div style={{ y: heroY, opacity: heroOpacity }} className="relative max-w-7xl mx-auto w-full">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            {/* Copy */}
            <div className="text-center lg:text-left pt-8 lg:pt-0">
              <motion.div
                initial={{ opacity: 0, y: 20, filter: 'blur(10px)' }}
                animate={{ opacity: 1, y: 0, filter: 'blur(0px)' }}
                transition={{ duration: 0.7, delay: 0.2, ease: EASE }}
                className="inline-flex items-center gap-2.5 px-5 py-2.5 rounded-full text-[13px] font-semibold mb-10"
                style={{ backgroundColor: `${COLORS.AUTUMN}15`, color: COLORS.AUTUMN, border: `1px solid ${COLORS.AUTUMN}25` }}
              >
                <Sparkles className="w-4 h-4" />
                Built for Fashion Brands
              </motion.div>

              <h1
                style={{ fontFamily: SERIF, lineHeight: 1.0, letterSpacing: '-0.02em' }}
                className="text-[2.75rem] sm:text-[3.75rem] lg:text-[4.5rem] xl:text-[5.5rem] mb-8"
              >
                {['Your Fashion Brand', 'Deserves Its'].map((line, i) => (
                  <span className="block overflow-hidden" key={i}>
                    <motion.span
                      className="block"
                      style={{ fontWeight: 400, color: COLORS.MIDNIGHT }}
                      initial={{ y: '120%', rotateX: -40 }}
                      animate={{ y: 0, rotateX: 0 }}
                      transition={{ duration: 1, delay: 0.3 + i * 0.12, ease: EASE }}
                    >
                      {line}
                    </motion.span>
                  </span>
                ))}
                <span className="block overflow-hidden">
                  <motion.span
                    className="block italic"
                    style={{ color: COLORS.AUTUMN, fontWeight: 400 }}
                    initial={{ y: '120%', rotateX: -40 }}
                    animate={{ y: 0, rotateX: 0 }}
                    transition={{ duration: 1, delay: 0.54, ease: EASE }}
                  >
                    Own Stage
                  </motion.span>
                </span>
              </h1>

              <motion.p
                initial={{ opacity: 0, y: 25, filter: 'blur(8px)' }}
                animate={{ opacity: 1, y: 0, filter: 'blur(0px)' }}
                transition={{ duration: 0.9, delay: 0.7, ease: EASE }}
                className="text-lg sm:text-xl mb-10 max-w-lg mx-auto lg:mx-0"
                style={{ color: COLORS.TEXT_MUTED, lineHeight: 1.75, fontWeight: 400 }}
              >
                Stop selling through DMs. Get your own professional website
                at <span className="font-semibold" style={{ color: COLORS.MIDNIGHT }}>yourbrand.vasstr.com</span> — live in 5 minutes.
              </motion.p>

              <motion.div
                initial={{ opacity: 0, y: 25 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.85, ease: EASE }}
                className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start mb-10"
              >
                <MagneticWrap>
                  <Link to="/Register">
                    <motion.button
                      whileHover={{ scale: 1.04, boxShadow: `0 28px 50px -12px ${COLORS.AUTUMN}60` }}
                      whileTap={{ scale: 0.97 }}
                      className="group relative px-8 py-4 rounded-xl font-bold text-white text-[15px] flex items-center gap-2.5 w-full sm:w-auto justify-center overflow-hidden"
                      style={{ backgroundColor: COLORS.AUTUMN, boxShadow: `0 16px 40px -8px ${COLORS.AUTUMN}40` }}
                    >
                      {/* Glow effect */}
                      <motion.div
                        className="absolute inset-0 rounded-xl"
                        style={{ background: `radial-gradient(circle at center, ${COLORS.AUTUMN_LIGHT}40 0%, transparent 70%)` }}
                        initial={{ opacity: 0 }}
                        whileHover={{ opacity: 1 }}
                        transition={{ duration: 0.3 }}
                      />
                      <span className="relative z-10 flex items-center gap-2.5">
                        Start Free — No Card Needed
                        <ArrowRight className="w-[18px] h-[18px] group-hover:translate-x-1.5 transition-transform duration-300" />
                      </span>
                    </motion.button>
                  </Link>
                </MagneticWrap>
                <motion.button
                  whileHover={{ scale: 1.04, backgroundColor: `${COLORS.MIDNIGHT}08` }}
                  whileTap={{ scale: 0.97 }}
                  onClick={() => setShowVideoModal(true)}
                  className="px-8 py-4 rounded-xl font-semibold text-[15px] flex items-center gap-2.5 w-full sm:w-auto justify-center transition-all duration-300"
                  style={{ color: COLORS.MIDNIGHT, border: `1px solid ${COLORS.MIDNIGHT}25`, backgroundColor: 'transparent' }}
                >
                  <div className="w-8 h-8 rounded-full flex items-center justify-center" style={{ border: `1.5px solid ${COLORS.MIDNIGHT}30` }}>
                    <Play className="w-3.5 h-3.5 ml-0.5" />
                  </div>
                  Watch Demo
                </motion.button>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 15 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 1.1, duration: 0.8 }}
                className="flex flex-wrap gap-6 text-[13px] font-medium justify-center lg:justify-start"
                style={{ color: COLORS.TEXT_MUTED }}
              >
                {['Free 30-day trial', 'Live in 5 minutes', 'No coding needed'].map((t, i) => (
                  <motion.span
                    key={i}
                    className="flex items-center gap-2"
                    initial={{ opacity: 0, x: -10 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 1.2 + i * 0.1, duration: 0.5 }}
                  >
                    <Check className="w-4 h-4" style={{ color: COLORS.AUTUMN_LIGHT }} />{t}
                  </motion.span>
                ))}
              </motion.div>
            </div>

            {/* Device Mockup */}
            <motion.div
              initial={{ opacity: 0, x: 80, rotateY: -15 }}
              animate={{ opacity: 1, x: 0, rotateY: 0 }}
              transition={{ duration: 1.4, delay: 0.4, ease: EASE }}
              className="relative hidden lg:block"
              style={{ perspective: 1200 }}
            >
              <motion.div
                whileHover={{ rotateY: -3, scale: 1.02 }}
                transition={{ type: 'spring', stiffness: 200, damping: 20 }}
                className="relative rounded-[16px] overflow-hidden"
                style={{ backgroundColor: '#1a1a1a', boxShadow: '0 60px 120px -30px rgba(0,0,0,0.6)' }}
              >
                <div className="flex items-center gap-1.5 px-4 py-3">
                  <div className="w-2.5 h-2.5 rounded-full bg-[#ff5f57]" />
                  <div className="w-2.5 h-2.5 rounded-full bg-[#febc2e]" />
                  <div className="w-2.5 h-2.5 rounded-full bg-[#28c840]" />
                  <div className="flex-1 mx-8 bg-[#2a2a2a] rounded-md px-4 py-1.5 text-[11px] text-[#666] text-center font-medium" style={{ letterSpacing: '0.05em' }}>
                    meraki.vasstr.com
                  </div>
                </div>
                <img src={tplEthnic} alt="Vasstr store preview" className="w-full h-auto block" />
              </motion.div>

              {/* Floating mobile */}
              <motion.div
                animate={{ y: [0, -12, 0] }}
                transition={{ duration: 5, repeat: Infinity, ease: 'easeInOut' }}
                className="absolute -bottom-8 -left-12 w-[130px] rounded-[20px] overflow-hidden"
                style={{ backgroundColor: '#1a1a1a', boxShadow: '0 40px 80px -20px rgba(0,0,0,0.5)', padding: '6px' }}
              >
                <div className="w-8 h-1 bg-[#333] rounded-full mx-auto mb-1" />
                <div className="rounded-[16px] overflow-hidden">
                  <img src={tplMinimal} alt="Mobile preview" className="w-full h-auto block" />
                </div>
              </motion.div>

              {/* Floating badge — Store Live */}
              <motion.div
                initial={{ opacity: 0, scale: 0.5, y: 20 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                transition={{ delay: 1.4, type: 'spring', stiffness: 200, damping: 15 }}
                className="absolute -top-4 -right-4 backdrop-blur-xl rounded-2xl px-4 py-3 flex items-center gap-3"
                style={{ backgroundColor: 'rgba(255,255,255,0.95)', boxShadow: '0 20px 50px -12px rgba(0,0,0,0.15)' }}
              >
                <div className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ backgroundColor: `${COLORS.AUTUMN}12` }}>
                  <Check className="w-5 h-5" style={{ color: COLORS.AUTUMN }} />
                </div>
                <div>
                  <div className="text-[13px] font-bold" style={{ color: COLORS.TEXT_DARK }}>Store is Live!</div>
                  <div className="text-[11px] font-medium" style={{ color: COLORS.TEXT_MUTED }}>Ready to sell</div>
                </div>
              </motion.div>

              {/* Floating badge — New Order */}
              <motion.div
                initial={{ opacity: 0, scale: 0.5, y: 20 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                transition={{ delay: 1.8, type: 'spring', stiffness: 200, damping: 15 }}
                className="absolute bottom-24 -right-6 backdrop-blur-xl rounded-2xl px-4 py-3 flex items-center gap-3"
                style={{ backgroundColor: 'rgba(255,255,255,0.95)', boxShadow: '0 20px 50px -12px rgba(0,0,0,0.15)' }}
              >
                <div className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ backgroundColor: `${COLORS.AUTUMN}12` }}>
                  <Package className="w-5 h-5" style={{ color: COLORS.AUTUMN }} />
                </div>
                <div>
                  <div className="text-[13px] font-bold" style={{ color: COLORS.TEXT_DARK }}>New Order!</div>
                  <div className="text-[11px] font-medium" style={{ color: COLORS.TEXT_MUTED }}>Just now</div>
                </div>
              </motion.div>
            </motion.div>
          </div>
        </motion.div>
      </section>

      {/* ══════════════════════════════════════════════
          SOCIAL PROOF — Infinite Marquee
      ══════════════════════════════════════════════ */}
      <section className="py-5 sm:py-6" style={{ backgroundColor: COLORS.LINEN }}>
        <Marquee speed={25}>
          {socialProofItems.map((item, i) => (
            <div key={i} className="flex items-center gap-2.5 px-8 shrink-0">
              <item.icon className="w-[16px] h-[16px]" style={{ color: COLORS.AUTUMN }} />
              <span className="text-[13px] font-semibold whitespace-nowrap" style={{ color: COLORS.TEXT_MUTED, letterSpacing: '0.03em' }}>
                {item.text}
              </span>
              <span className="ml-6 text-[8px]" style={{ color: COLORS.AUTUMN, opacity: 0.35 }}>◆</span>
            </div>
          ))}
        </Marquee>
      </section>

      {/* ══════════════════════════════════════════════
          SOUND FAMILIAR — Before / After
      ══════════════════════════════════════════════ */}
      <section className="py-24 sm:py-32 px-4 sm:px-6 lg:px-8" style={{ backgroundColor: '#fff' }}>
        <div className="max-w-6xl mx-auto">
          <Reveal className="text-center mb-16">
            <Heading>Sound Familiar?</Heading>
            <p className="text-lg mt-5 max-w-2xl mx-auto" style={{ color: COLORS.TEXT_MUTED, lineHeight: 1.7 }}>
              Most fashion designers are stuck running their entire business through Instagram DMs
            </p>
          </Reveal>

          <div className="grid md:grid-cols-2 gap-6 lg:gap-8 max-w-4xl mx-auto">
            {/* Without */}
            <Reveal direction="left" delay={0.1}>
              <div className="rounded-2xl p-7 sm:p-9 h-full" style={{ backgroundColor: COLORS.LINEN }}>
                <div className="text-[11px] font-bold uppercase mb-6" style={{ letterSpacing: '0.15em', color: COLORS.TEXT_MUTED }}>
                  Without Vasstr
                </div>
                <ul className="space-y-4">
                  {[
                    'Managing orders through WhatsApp/Instagram DMs',
                    'No proper product catalog — sending photos one by one',
                    "Customers can't browse or pay on their own",
                    'Losing track of orders and payments',
                    'Looking unprofessional compared to bigger brands',
                  ].map((item, i) => (
                    <motion.li
                      key={i}
                      className="flex items-start gap-3"
                      initial={{ opacity: 0, x: -30 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      viewport={{ once: true }}
                      transition={{ delay: 0.3 + i * 0.08, duration: 0.5, ease: EASE }}
                    >
                      <X className="w-4 h-4 mt-1 flex-shrink-0" style={{ color: COLORS.TEXT_MUTED }} />
                      <span className="text-[15px]" style={{ color: COLORS.TEXT_MUTED, lineHeight: 1.6 }}>{item}</span>
                    </motion.li>
                  ))}
                </ul>
              </div>
            </Reveal>

            {/* With */}
            <Reveal direction="right" delay={0.2}>
              <div className="rounded-2xl p-7 sm:p-9 h-full" style={{ backgroundColor: '#fff', border: `2px solid ${COLORS.AUTUMN}20` }}>
                <div className="text-[11px] font-bold uppercase mb-6" style={{ letterSpacing: '0.15em', color: COLORS.AUTUMN }}>
                  With Vasstr
                </div>
                <ul className="space-y-4">
                  {[
                    'Professional storefront at yourbrand.vasstr.com',
                    'Beautiful product catalog customers can browse 24/7',
                    'Customers order and pay directly — no back and forth',
                    'Dashboard to track every order, payment & customer',
                    'You look like an established brand from Day 1',
                  ].map((item, i) => (
                    <motion.li
                      key={i}
                      className="flex items-start gap-3"
                      initial={{ opacity: 0, x: 30 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      viewport={{ once: true }}
                      transition={{ delay: 0.3 + i * 0.08, duration: 0.5, ease: EASE }}
                    >
                      <motion.div
                        initial={{ scale: 0 }}
                        whileInView={{ scale: 1 }}
                        viewport={{ once: true }}
                        transition={{ delay: 0.4 + i * 0.08, type: 'spring', stiffness: 300, damping: 15 }}
                      >
                        <Check className="w-4 h-4 mt-1 flex-shrink-0" style={{ color: COLORS.AUTUMN }} />
                      </motion.div>
                      <span className="text-[15px]" style={{ color: COLORS.TEXT_DARK, lineHeight: 1.6 }}>{item}</span>
                    </motion.li>
                  ))}
                </ul>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════════════
          HOW IT WORKS — MIDNIGHT with spring animations
      ══════════════════════════════════════════════ */}
      <section className="relative py-24 sm:py-32 px-4 sm:px-6 lg:px-8 overflow-hidden" style={{ backgroundColor: '#fff' }}>
        <motion.div
          className="absolute w-[500px] h-[500px] rounded-full blur-[130px]"
          style={{ backgroundColor: `${COLORS.AUTUMN}06`, top: '50%', left: '50%', transform: 'translate(-50%, -50%)' }}
          animate={{ scale: [1, 1.2, 1], opacity: [0.5, 0.8, 0.5] }}
          transition={{ duration: 8, repeat: Infinity, ease: 'easeInOut' }}
        />

        <div className="relative max-w-6xl mx-auto">
          <Reveal className="text-center mb-16">
            <motion.div
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-[13px] font-semibold mb-6"
              style={{ backgroundColor: `${COLORS.AUTUMN}12`, color: COLORS.AUTUMN, border: `1px solid ${COLORS.AUTUMN}20` }}
              whileHover={{ scale: 1.05 }}
            >
              Takes 5 Minutes
            </motion.div>
            <Heading>Three Steps. That's It.</Heading>
            <p className="text-lg mt-5" style={{ color: COLORS.TEXT_MUTED, lineHeight: 1.7 }}>
              No technical skills needed. Seriously.
            </p>
          </Reveal>

          <StaggerWrap className="grid md:grid-cols-3 gap-6 lg:gap-8" stagger={0.2}>
            {[
              { step: '01', icon: Crown, title: 'Sign Up & Pick a Template', desc: 'Create your account, choose from 20+ fashion-ready templates. Your branded URL goes live instantly.' },
              { step: '02', icon: Upload, title: 'Upload Your Designs', desc: 'Add photos, prices, and descriptions — as easy as posting on Instagram. Products go live immediately.' },
              { step: '03', icon: Rocket, title: 'Share & Start Selling', desc: 'Share your website link. Customers browse, order, and pay directly. You manage everything from one dashboard.' },
            ].map((item, i) => (
              <StaggerChild key={i}>
                <motion.div
                  whileHover={{ y: -8, boxShadow: '0 25px 50px -12px rgba(0,0,0,0.08)' }}
                  className="rounded-2xl p-7 sm:p-8 h-full transition-all duration-500 cursor-default"
                  style={{ backgroundColor: COLORS.LINEN, border: `1px solid ${COLORS.AUTUMN}08` }}
                >
                  <div className="flex items-center justify-between mb-6">
                    <motion.div
                      className="w-12 h-12 rounded-xl flex items-center justify-center"
                      style={{ backgroundColor: `${COLORS.AUTUMN}15` }}
                      whileHover={{ rotate: 5, scale: 1.1 }}
                      transition={{ type: 'spring', stiffness: 300, damping: 15 }}
                    >
                      <item.icon className="w-5 h-5" style={{ color: COLORS.AUTUMN }} />
                    </motion.div>
                    <motion.span
                      className="text-[3rem]"
                      style={{ fontFamily: SERIF, fontWeight: 300, color: `${COLORS.AUTUMN}12` }}
                      initial={{ scale: 0.5, opacity: 0 }}
                      whileInView={{ scale: 1, opacity: 1 }}
                      viewport={{ once: true }}
                      transition={{ delay: 0.3 + i * 0.15, type: 'spring', stiffness: 150, damping: 12 }}
                    >
                      {item.step}
                    </motion.span>
                  </div>
                  <h3 className="text-xl font-bold mb-3" style={{ letterSpacing: '-0.01em', lineHeight: 1.25, color: COLORS.TEXT_DARK }}>
                    {item.title}
                  </h3>
                  <p className="text-[15px]" style={{ color: COLORS.TEXT_MUTED, lineHeight: 1.7 }}>{item.desc}</p>
                </motion.div>
              </StaggerChild>
            ))}
          </StaggerWrap>
        </div>
      </section>

      {/* ══════════════════════════════════════════════
          JOURNEY — Visual Process Walkthrough
      ══════════════════════════════════════════════ */}
      <section className="py-24 sm:py-32 px-4 sm:px-6 lg:px-8" style={{ backgroundColor: '#fff' }}>
        <div className="max-w-6xl mx-auto">
          <Reveal className="text-center mb-20">
            <Heading>
              From Sign Up to<br />
              <span style={{ color: COLORS.AUTUMN, fontStyle: 'italic' }}>Your First Sale</span>
            </Heading>
            <p className="text-lg mt-5 max-w-2xl mx-auto" style={{ color: COLORS.TEXT_MUTED, lineHeight: 1.7 }}>
              Here's exactly what happens when you join Vasstr — see every step before you start
            </p>
          </Reveal>

          <div className="space-y-20 lg:space-y-28">
            {/* Step 1 — Sign Up */}
            <div className="flex flex-col lg:flex-row gap-10 lg:gap-16 items-center">
              <Reveal direction="left" className="flex-1 text-center lg:text-left">
                <motion.div
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-[12px] font-bold mb-5"
                  style={{ backgroundColor: `${COLORS.AUTUMN}12`, color: COLORS.AUTUMN }}
                  whileHover={{ scale: 1.05 }}
                >
                  Step 1 · 30 seconds
                </motion.div>
                <h3 className="text-2xl sm:text-3xl font-bold mb-4" style={{ fontFamily: SERIF, color: COLORS.TEXT_DARK }}>
                  Create Your Account
                </h3>
                <p className="text-[16px] max-w-md mx-auto lg:mx-0 mb-6" style={{ color: COLORS.TEXT_MUTED, lineHeight: 1.75 }}>
                  Enter your brand name and email. That's it — no credit card, no complex forms. You're in within 30 seconds.
                </p>
                <div className="flex flex-wrap gap-3 justify-center lg:justify-start">
                  {['No card needed', 'Instant access', 'Free for 30 days'].map((t, i) => (
                    <span key={i} className="flex items-center gap-1.5 text-[12px] font-medium px-3 py-1.5 rounded-full"
                      style={{ backgroundColor: `${COLORS.AUTUMN}08`, color: COLORS.AUTUMN }}>
                      <Check className="w-3 h-3" /> {t}
                    </span>
                  ))}
                </div>
              </Reveal>

              <Reveal direction="right" className="flex-1 w-full max-w-lg">
                <motion.div
                  whileHover={{ y: -4 }}
                  className="rounded-2xl p-6 sm:p-8"
                  style={{ backgroundColor: COLORS.LINEN, border: `1px solid ${COLORS.AUTUMN}10` }}
                >
                  <div className="text-[13px] font-bold mb-5" style={{ color: COLORS.TEXT_DARK }}>Start your fashion brand website</div>
                  <div className="space-y-3 mb-5">
                    {['Brand Name', 'Email Address', 'Password'].map((label, i) => (
                      <motion.div
                        key={i}
                        initial={{ opacity: 0, x: 20 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        viewport={{ once: true }}
                        transition={{ delay: 0.3 + i * 0.1, duration: 0.5, ease: EASE }}
                      >
                        <div className="text-[11px] font-semibold mb-1.5" style={{ color: COLORS.TEXT_MUTED }}>{label}</div>
                        <div className="bg-white rounded-lg h-10 px-3 flex items-center" style={{ border: '1px solid #e5e5e5' }}>
                          <span className="text-[13px]" style={{ color: i === 0 ? COLORS.TEXT_DARK : '#ccc' }}>
                            {i === 0 ? 'Meraki Fashions' : i === 1 ? 'priya@meraki.com' : '••••••••'}
                          </span>
                        </div>
                      </motion.div>
                    ))}
                  </div>
                  <motion.div
                    className="w-full py-3 rounded-lg text-center text-[13px] font-bold text-white"
                    style={{ backgroundColor: COLORS.AUTUMN }}
                    whileHover={{ scale: 1.02 }}
                  >
                    Create My Store →
                  </motion.div>
                  <div className="text-center text-[11px] mt-3" style={{ color: COLORS.TEXT_MUTED }}>
                    Your store: <span className="font-semibold" style={{ color: COLORS.AUTUMN }}>merakifashions.vasstr.com</span>
                  </div>
                </motion.div>
              </Reveal>
            </div>

            {/* Step 2 — Pick Template */}
            <div className="flex flex-col lg:flex-row-reverse gap-10 lg:gap-16 items-center">
              <Reveal direction="right" className="flex-1 text-center lg:text-left">
                <motion.div
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-[12px] font-bold mb-5"
                  style={{ backgroundColor: `${COLORS.AUTUMN}12`, color: COLORS.AUTUMN }}
                  whileHover={{ scale: 1.05 }}
                >
                  Step 2 · 1 minute
                </motion.div>
                <h3 className="text-2xl sm:text-3xl font-bold mb-4" style={{ fontFamily: SERIF, color: COLORS.TEXT_DARK }}>
                  Pick Your Look
                </h3>
                <p className="text-[16px] max-w-md mx-auto lg:mx-0 mb-6" style={{ color: COLORS.TEXT_MUTED, lineHeight: 1.75 }}>
                  Browse 20+ professionally designed templates made specifically for Indian fashion brands. One click to apply — your website updates instantly.
                </p>
                <div className="flex flex-wrap gap-3 justify-center lg:justify-start">
                  {['20+ templates', 'Fashion-focused', 'One-click apply'].map((t, i) => (
                    <span key={i} className="flex items-center gap-1.5 text-[12px] font-medium px-3 py-1.5 rounded-full"
                      style={{ backgroundColor: `${COLORS.AUTUMN}08`, color: COLORS.AUTUMN }}>
                      <Palette className="w-3 h-3" /> {t}
                    </span>
                  ))}
                </div>
              </Reveal>

              <Reveal direction="left" className="flex-1 w-full max-w-lg">
                <div className="grid grid-cols-3 gap-3">
                  {[
                    { img: tplMinimal, name: 'Minimal' },
                    { img: tplEthnic, name: 'Ethnic' },
                    { img: tplVibrant, name: 'Vibrant' },
                  ].map((tmpl, i) => (
                    <motion.div
                      key={i}
                      className="rounded-xl overflow-hidden cursor-pointer relative"
                      style={{
                        border: i === 0 ? `2px solid ${COLORS.AUTUMN}` : '2px solid transparent',
                        boxShadow: i === 0 ? `0 8px 25px -6px ${COLORS.AUTUMN}30` : '0 4px 12px -4px rgba(0,0,0,0.1)',
                      }}
                      initial={{ opacity: 0, y: 30 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                      transition={{ delay: 0.2 + i * 0.15, duration: 0.6, ease: EASE }}
                      whileHover={{ y: -4, scale: 1.03 }}
                    >
                      <div className="aspect-[3/4] overflow-hidden">
                        <img src={tmpl.img} alt={tmpl.name} className="w-full h-full object-cover object-top" />
                      </div>
                      <div className="p-2 text-center bg-white">
                        <div className="text-[11px] font-bold" style={{ color: COLORS.TEXT_DARK }}>{tmpl.name}</div>
                      </div>
                      {i === 0 && (
                        <motion.div
                          className="absolute top-2 right-2 w-5 h-5 rounded-full flex items-center justify-center"
                          style={{ backgroundColor: COLORS.AUTUMN }}
                          initial={{ scale: 0 }}
                          whileInView={{ scale: 1 }}
                          viewport={{ once: true }}
                          transition={{ delay: 0.8, type: 'spring', stiffness: 300, damping: 15 }}
                        >
                          <Check className="w-3 h-3 text-white" />
                        </motion.div>
                      )}
                    </motion.div>
                  ))}
                </div>
              </Reveal>
            </div>

            {/* Step 3 — Add Products */}
            <div className="flex flex-col lg:flex-row gap-10 lg:gap-16 items-center">
              <Reveal direction="left" className="flex-1 text-center lg:text-left">
                <motion.div
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-[12px] font-bold mb-5"
                  style={{ backgroundColor: `${COLORS.AUTUMN}12`, color: COLORS.AUTUMN }}
                  whileHover={{ scale: 1.05 }}
                >
                  Step 3 · 2 minutes per product
                </motion.div>
                <h3 className="text-2xl sm:text-3xl font-bold mb-4" style={{ fontFamily: SERIF, color: COLORS.TEXT_DARK }}>
                  Upload Your Designs
                </h3>
                <p className="text-[16px] max-w-md mx-auto lg:mx-0 mb-6" style={{ color: COLORS.TEXT_MUTED, lineHeight: 1.75 }}>
                  Add photos, set prices, add size options — as easy as posting on Instagram. Products go live on your store instantly.
                </p>
                <div className="flex flex-wrap gap-3 justify-center lg:justify-start">
                  {['Drag & drop upload', 'Multiple sizes', 'Instant publish'].map((t, i) => (
                    <span key={i} className="flex items-center gap-1.5 text-[12px] font-medium px-3 py-1.5 rounded-full"
                      style={{ backgroundColor: `${COLORS.AUTUMN}08`, color: COLORS.AUTUMN }}>
                      <Image className="w-3 h-3" /> {t}
                    </span>
                  ))}
                </div>
              </Reveal>

              <Reveal direction="right" className="flex-1 w-full max-w-lg">
                <motion.div
                  whileHover={{ y: -4 }}
                  className="rounded-2xl p-5 sm:p-6"
                  style={{ backgroundColor: COLORS.LINEN, border: `1px solid ${COLORS.AUTUMN}10` }}
                >
                  <div className="flex items-center justify-between mb-4">
                    <div className="text-[13px] font-bold" style={{ color: COLORS.TEXT_DARK }}>Add New Product</div>
                    <div className="text-[11px] font-semibold px-2.5 py-1 rounded-full" style={{ backgroundColor: `${COLORS.AUTUMN}12`, color: COLORS.AUTUMN }}>Draft</div>
                  </div>
                  <div className="grid grid-cols-3 gap-2 mb-4">
                    <motion.div
                      className="aspect-square rounded-lg overflow-hidden"
                      initial={{ opacity: 0, scale: 0.8 }}
                      whileInView={{ opacity: 1, scale: 1 }}
                      viewport={{ once: true }}
                      transition={{ delay: 0.3, duration: 0.5 }}
                    >
                      <img src={fashionRunway} alt="Product" className="w-full h-full object-cover" />
                    </motion.div>
                    <motion.div
                      className="aspect-square rounded-lg overflow-hidden"
                      initial={{ opacity: 0, scale: 0.8 }}
                      whileInView={{ opacity: 1, scale: 1 }}
                      viewport={{ once: true }}
                      transition={{ delay: 0.45, duration: 0.5 }}
                    >
                      <img src={fashionSaree} alt="Product" className="w-full h-full object-cover" />
                    </motion.div>
                    <motion.div
                      className="aspect-square rounded-lg flex flex-col items-center justify-center cursor-pointer"
                      style={{ border: `2px dashed ${COLORS.AUTUMN}40`, backgroundColor: 'white' }}
                      initial={{ opacity: 0, scale: 0.8 }}
                      whileInView={{ opacity: 1, scale: 1 }}
                      viewport={{ once: true }}
                      transition={{ delay: 0.6, duration: 0.5 }}
                      whileHover={{ borderColor: COLORS.AUTUMN, scale: 1.05 }}
                    >
                      <Upload className="w-5 h-5 mb-1" style={{ color: COLORS.AUTUMN }} />
                      <span className="text-[9px] font-semibold" style={{ color: COLORS.AUTUMN }}>Add More</span>
                    </motion.div>
                  </div>
                  <div className="space-y-2.5 mb-4">
                    <div className="bg-white rounded-lg h-9 px-3 flex items-center" style={{ border: '1px solid #e5e5e5' }}>
                      <span className="text-[12px] font-medium" style={{ color: COLORS.TEXT_DARK }}>Silk Embroidered Kurti Set</span>
                    </div>
                    <div className="flex gap-2">
                      <div className="bg-white rounded-lg h-9 px-3 flex items-center flex-1" style={{ border: '1px solid #e5e5e5' }}>
                        <span className="text-[12px] font-medium" style={{ color: COLORS.TEXT_DARK }}>₹2,450</span>
                      </div>
                      <div className="bg-white rounded-lg h-9 px-3 flex items-center flex-1" style={{ border: '1px solid #e5e5e5' }}>
                        <span className="text-[12px]" style={{ color: COLORS.TEXT_MUTED }}>S, M, L, XL</span>
                      </div>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <div className="flex-1 py-2.5 rounded-lg text-center text-[12px] font-semibold"
                      style={{ border: `1px solid ${COLORS.AUTUMN}`, color: COLORS.AUTUMN }}>
                      Save Draft
                    </div>
                    <motion.div
                      className="flex-1 py-2.5 rounded-lg text-center text-[12px] font-bold text-white"
                      style={{ backgroundColor: COLORS.AUTUMN }}
                      whileHover={{ scale: 1.02 }}
                    >
                      Publish →
                    </motion.div>
                  </div>
                </motion.div>
              </Reveal>
            </div>

            {/* Step 4 — Share & Sell */}
            <div className="flex flex-col lg:flex-row-reverse gap-10 lg:gap-16 items-center">
              <Reveal direction="right" className="flex-1 text-center lg:text-left">
                <motion.div
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-[12px] font-bold mb-5"
                  style={{ backgroundColor: `${COLORS.AUTUMN}12`, color: COLORS.AUTUMN }}
                  whileHover={{ scale: 1.05 }}
                >
                  Step 4 · You're live!
                </motion.div>
                <h3 className="text-2xl sm:text-3xl font-bold mb-4" style={{ fontFamily: SERIF, color: COLORS.TEXT_DARK }}>
                  Share & Start Earning
                </h3>
                <p className="text-[16px] max-w-md mx-auto lg:mx-0 mb-6" style={{ color: COLORS.TEXT_MUTED, lineHeight: 1.75 }}>
                  Your store is live! Share the link on Instagram bio, WhatsApp stories, anywhere. Customers browse, order, and pay directly — money goes straight to your bank.
                </p>
                <div className="flex flex-wrap gap-3 justify-center lg:justify-start">
                  {['Share anywhere', 'Direct payments', 'Track orders'].map((t, i) => (
                    <span key={i} className="flex items-center gap-1.5 text-[12px] font-medium px-3 py-1.5 rounded-full"
                      style={{ backgroundColor: `${COLORS.AUTUMN}08`, color: COLORS.AUTUMN }}>
                      <Share2 className="w-3 h-3" /> {t}
                    </span>
                  ))}
                </div>
              </Reveal>

              <Reveal direction="left" className="flex-1 w-full max-w-lg">
                <motion.div whileHover={{ y: -4 }} className="rounded-2xl p-5 sm:p-6" style={{ backgroundColor: COLORS.LINEN, border: `1px solid ${COLORS.AUTUMN}10` }}>
                  <div className="text-[13px] font-bold mb-4" style={{ color: COLORS.TEXT_DARK }}>Your store is ready!</div>
                  {/* Link bar */}
                  <motion.div
                    className="bg-white rounded-lg px-4 py-3 flex items-center justify-between mb-5"
                    style={{ border: `2px solid ${COLORS.AUTUMN}25` }}
                    initial={{ opacity: 0 }}
                    whileInView={{ opacity: 1 }}
                    viewport={{ once: true }}
                    transition={{ delay: 0.3, duration: 0.5 }}
                  >
                    <div className="flex items-center gap-2">
                      <Globe className="w-4 h-4" style={{ color: COLORS.AUTUMN }} />
                      <span className="text-[13px] font-semibold" style={{ color: COLORS.TEXT_DARK }}>merakifashions.vasstr.com</span>
                    </div>
                    <motion.div
                      className="px-3 py-1.5 rounded-md text-[11px] font-bold text-white cursor-pointer"
                      style={{ backgroundColor: COLORS.AUTUMN }}
                      whileHover={{ scale: 1.05 }}
                    >
                      Copy
                    </motion.div>
                  </motion.div>
                  {/* Share options */}
                  <div className="text-[11px] font-semibold mb-3" style={{ color: COLORS.TEXT_MUTED, letterSpacing: '0.05em' }}>SHARE ON</div>
                  <div className="flex gap-2 mb-5">
                    {[
                      { name: 'Instagram Bio', icon: Instagram, bg: '#E4405F' },
                      { name: 'WhatsApp', icon: MessageCircle, bg: '#25D366' },
                      { name: 'Facebook', icon: Share2, bg: '#1877F2' },
                    ].map((s, i) => (
                      <motion.div
                        key={i}
                        className="flex-1 flex items-center justify-center gap-1.5 py-2.5 rounded-lg text-white text-[11px] font-bold cursor-pointer"
                        style={{ backgroundColor: s.bg }}
                        initial={{ opacity: 0, y: 15 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ delay: 0.4 + i * 0.1, duration: 0.4 }}
                        whileHover={{ scale: 1.03, y: -2 }}
                      >
                        <s.icon className="w-3.5 h-3.5" />
                        {s.name}
                      </motion.div>
                    ))}
                  </div>
                  {/* Notification */}
                  <motion.div
                    className="bg-white rounded-lg px-4 py-3 flex items-center gap-3"
                    style={{ boxShadow: '0 4px 12px -4px rgba(0,0,0,0.08)' }}
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: 0.8, type: 'spring', stiffness: 200, damping: 15 }}
                  >
                    <motion.div
                      className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0"
                      style={{ backgroundColor: '#10b98115' }}
                      animate={{ scale: [1, 1.15, 1] }}
                      transition={{ duration: 2, repeat: Infinity }}
                    >
                      <ShoppingBag className="w-4 h-4" style={{ color: '#10b981' }} />
                    </motion.div>
                    <div>
                      <div className="text-[12px] font-bold" style={{ color: COLORS.TEXT_DARK }}>New Order! Priya M. placed an order</div>
                      <div className="text-[10px]" style={{ color: COLORS.TEXT_MUTED }}>Silk Embroidered Kurti Set · ₹2,450</div>
                    </div>
                  </motion.div>
                </motion.div>
              </Reveal>
            </div>
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════════════
          DASHBOARD PREVIEW — Your Command Center
      ══════════════════════════════════════════════ */}
      <section className="py-24 sm:py-32 px-4 sm:px-6 lg:px-8" style={{ backgroundColor: COLORS.LINEN }}>
        <div className="max-w-6xl mx-auto">
          <Reveal className="text-center mb-16">
            <Heading>
              Your Business,<br />
              <span style={{ color: COLORS.AUTUMN, fontStyle: 'italic' }}>At a Glance</span>
            </Heading>
            <p className="text-lg mt-5 max-w-2xl mx-auto" style={{ color: COLORS.TEXT_MUTED, lineHeight: 1.7 }}>
              Track revenue, manage orders, and understand your customers — all from one powerful dashboard
            </p>
          </Reveal>

          {/* Dashboard Mockup in Browser Frame */}
          <Reveal direction="scale">
            <div className="rounded-2xl overflow-hidden" style={{ backgroundColor: '#1a1a1a', boxShadow: '0 60px 120px -30px rgba(0,0,0,0.25)' }}>
              {/* Browser chrome */}
              <div className="flex items-center gap-1.5 px-4 py-3">
                <div className="w-2.5 h-2.5 rounded-full bg-[#ff5f57]" />
                <div className="w-2.5 h-2.5 rounded-full bg-[#febc2e]" />
                <div className="w-2.5 h-2.5 rounded-full bg-[#28c840]" />
                <div className="flex-1 mx-8 bg-[#2a2a2a] rounded-md px-4 py-1.5 text-[11px] text-[#666] text-center font-medium" style={{ letterSpacing: '0.05em' }}>
                  vasstr.com/dashboard
                </div>
              </div>

              {/* Dashboard content */}
              <div className="p-4 sm:p-6 lg:p-8" style={{ backgroundColor: '#f8f9fb' }}>
                {/* Greeting */}
                <motion.div
                  className="flex items-center justify-between mb-6"
                  initial={{ opacity: 0, y: -10 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.2, duration: 0.5 }}
                >
                  <div>
                    <div className="text-[15px] sm:text-[17px] font-bold" style={{ color: COLORS.TEXT_DARK }}>Good morning, Priya! 👋</div>
                    <div className="text-[12px]" style={{ color: COLORS.TEXT_MUTED }}>Here's how your store is performing</div>
                  </div>
                  <div className="hidden sm:flex items-center gap-2 px-3 py-1.5 rounded-lg text-[11px] font-semibold"
                    style={{ backgroundColor: '#10b98112', color: '#10b981' }}>
                    <div className="w-1.5 h-1.5 rounded-full bg-[#10b981]" />
                    Store is Live
                  </div>
                </motion.div>

                {/* Stats Grid */}
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mb-6">
                  {[
                    { label: 'Total Revenue', value: 184500, prefix: '₹', trend: '+23%', icon: IndianRupee, color: COLORS.AUTUMN },
                    { label: 'Total Orders', value: 127, prefix: '', trend: '+18%', icon: ShoppingBag, color: '#10b981' },
                    { label: 'Active Customers', value: 89, prefix: '', trend: '+12%', icon: Users, color: '#3b82f6' },
                    { label: 'Conversion Rate', value: 42, prefix: '', suffix: '%', trend: '+2.1%', icon: TrendingUp, color: '#8b5cf6', divide: 10 },
                  ].map((stat, i) => (
                    <motion.div
                      key={i}
                      className="bg-white rounded-xl p-4 sm:p-5"
                      style={{ boxShadow: '0 1px 3px rgba(0,0,0,0.04)' }}
                      initial={{ opacity: 0, y: 25 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                      transition={{ delay: 0.3 + i * 0.1, duration: 0.6, ease: EASE }}
                      whileHover={{ y: -3, boxShadow: '0 8px 25px -6px rgba(0,0,0,0.08)' }}
                    >
                      <div className="flex items-center justify-between mb-3">
                        <div className="text-[11px] font-semibold" style={{ color: COLORS.TEXT_MUTED, letterSpacing: '0.02em' }}>{stat.label}</div>
                        <div className="w-7 h-7 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${stat.color}12` }}>
                          <stat.icon className="w-3.5 h-3.5" style={{ color: stat.color }} />
                        </div>
                      </div>
                      <div className="text-xl sm:text-2xl font-extrabold mb-1" style={{ color: COLORS.TEXT_DARK, letterSpacing: '-0.02em' }}>
                        {stat.divide ? <><CountUp end={stat.value} prefix={stat.prefix} /><span className="text-lg">.{stat.divide}</span></> : <CountUp end={stat.value} prefix={stat.prefix} />}{stat.suffix || ''}
                      </div>
                      <div className="flex items-center gap-1 text-[11px] font-semibold" style={{ color: stat.color }}>
                        <TrendingUp className="w-3 h-3" />
                        {stat.trend} this month
                      </div>
                    </motion.div>
                  ))}
                </div>

                {/* Chart + Recent Orders */}
                <div className="grid lg:grid-cols-5 gap-4 sm:gap-5">
                  {/* Revenue Chart */}
                  <motion.div
                    className="lg:col-span-3 bg-white rounded-xl p-5"
                    style={{ boxShadow: '0 1px 3px rgba(0,0,0,0.04)' }}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: 0.5, duration: 0.6 }}
                  >
                    <div className="flex items-center justify-between mb-5">
                      <div className="text-[13px] font-bold" style={{ color: COLORS.TEXT_DARK }}>Revenue Overview</div>
                      <div className="flex gap-1.5">
                        {['6M', '1Y', 'All'].map((p, i) => (
                          <div key={i} className="text-[10px] font-semibold px-2.5 py-1 rounded-md cursor-pointer"
                            style={i === 1 ? { backgroundColor: `${COLORS.AUTUMN}12`, color: COLORS.AUTUMN } : { color: COLORS.TEXT_MUTED }}>
                            {p}
                          </div>
                        ))}
                      </div>
                    </div>
                    <div className="flex items-end gap-[6px] sm:gap-2 h-28 sm:h-36">
                      {[35, 52, 45, 68, 42, 78, 65, 85, 72, 92, 88, 95].map((h, i) => (
                        <motion.div
                          key={i}
                          className="flex-1 rounded-t-md cursor-pointer relative group"
                          style={{ backgroundColor: i === 11 ? COLORS.AUTUMN : `${COLORS.AUTUMN}25` }}
                          initial={{ height: 0 }}
                          whileInView={{ height: `${h}%` }}
                          viewport={{ once: true }}
                          transition={{ delay: 0.6 + i * 0.04, duration: 0.5, ease: EASE }}
                          whileHover={{ backgroundColor: COLORS.AUTUMN, scaleY: 1.05 }}
                        />
                      ))}
                    </div>
                    <div className="flex justify-between mt-3 text-[10px] font-medium" style={{ color: COLORS.TEXT_MUTED }}>
                      {['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'].map(m => (
                        <span key={m}>{m}</span>
                      ))}
                    </div>
                  </motion.div>

                  {/* Recent Orders */}
                  <motion.div
                    className="lg:col-span-2 bg-white rounded-xl p-5"
                    style={{ boxShadow: '0 1px 3px rgba(0,0,0,0.04)' }}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: 0.6, duration: 0.6 }}
                  >
                    <div className="flex items-center justify-between mb-4">
                      <div className="text-[13px] font-bold" style={{ color: COLORS.TEXT_DARK }}>Recent Orders</div>
                      <div className="text-[11px] font-semibold cursor-pointer" style={{ color: COLORS.AUTUMN }}>View All</div>
                    </div>
                    <div className="space-y-0">
                      {[
                        { id: '#1247', customer: 'Priya M.', amount: '₹2,450', status: 'Delivered', icon: Check, statusColor: '#10b981' },
                        { id: '#1246', customer: 'Anita S.', amount: '₹1,890', status: 'Shipped', icon: Truck, statusColor: '#3b82f6' },
                        { id: '#1245', customer: 'Riya K.', amount: '₹3,200', status: 'Processing', icon: Clock, statusColor: COLORS.AUTUMN },
                        { id: '#1244', customer: 'Meera D.', amount: '₹1,650', status: 'Delivered', icon: Check, statusColor: '#10b981' },
                        { id: '#1243', customer: 'Kavya R.', amount: '₹2,100', status: 'Shipped', icon: Truck, statusColor: '#3b82f6' },
                      ].map((order, i) => (
                        <motion.div
                          key={i}
                          className="flex items-center justify-between py-2.5"
                          style={{ borderBottom: i < 4 ? '1px solid #f3f3f3' : 'none' }}
                          initial={{ opacity: 0, x: 20 }}
                          whileInView={{ opacity: 1, x: 0 }}
                          viewport={{ once: true }}
                          transition={{ delay: 0.7 + i * 0.06, duration: 0.4 }}
                        >
                          <div className="flex items-center gap-2.5">
                            <div className="w-7 h-7 rounded-full flex items-center justify-center" style={{ backgroundColor: `${order.statusColor}10` }}>
                              <order.icon className="w-3 h-3" style={{ color: order.statusColor }} />
                            </div>
                            <div>
                              <div className="text-[12px] font-semibold" style={{ color: COLORS.TEXT_DARK }}>{order.customer}</div>
                              <div className="text-[10px]" style={{ color: COLORS.TEXT_MUTED }}>{order.id}</div>
                            </div>
                          </div>
                          <div className="text-right">
                            <div className="text-[12px] font-bold" style={{ color: COLORS.TEXT_DARK }}>{order.amount}</div>
                            <div className="text-[10px] font-semibold" style={{ color: order.statusColor }}>{order.status}</div>
                          </div>
                        </motion.div>
                      ))}
                    </div>
                  </motion.div>
                </div>
              </div>
            </div>
          </Reveal>

          {/* Dashboard features callout */}
          <StaggerWrap className="grid sm:grid-cols-3 gap-4 mt-10" stagger={0.1}>
            {[
              { icon: BarChart3, title: 'Revenue Analytics', desc: 'Track daily, weekly, monthly revenue with visual charts' },
              { icon: Package, title: 'Order Management', desc: 'Manage orders from placed to delivered — all in one place' },
              { icon: Users, title: 'Customer Insights', desc: 'Know your customers — their orders, preferences, and history' },
            ].map((f, i) => (
              <StaggerChild key={i} direction={['left', 'up', 'right'][i]}>
                <motion.div
                  whileHover={{ y: -4, boxShadow: '0 12px 30px -8px rgba(0,0,0,0.08)' }}
                  className="bg-white rounded-xl p-5 text-center cursor-default"
                  style={{ border: '1px solid rgba(0,0,0,0.04)' }}
                >
                  <motion.div
                    className="w-11 h-11 rounded-xl flex items-center justify-center mx-auto mb-3"
                    style={{ backgroundColor: `${COLORS.AUTUMN}10` }}
                    whileHover={{ rotate: 8, scale: 1.1 }}
                    transition={{ type: 'spring', stiffness: 300, damping: 15 }}
                  >
                    <f.icon className="w-5 h-5" style={{ color: COLORS.AUTUMN }} />
                  </motion.div>
                  <h4 className="text-[14px] font-bold mb-1.5" style={{ color: COLORS.TEXT_DARK }}>{f.title}</h4>
                  <p className="text-[13px]" style={{ color: COLORS.TEXT_MUTED, lineHeight: 1.5 }}>{f.desc}</p>
                </motion.div>
              </StaggerChild>
            ))}
          </StaggerWrap>
        </div>
      </section>

      {/* ══════════════════════════════════════════════
          TEMPLATE SHOWCASE — 3D Tilt Cards
      ══════════════════════════════════════════════ */}
      <section className="py-24 sm:py-32 px-4 sm:px-6 lg:px-8" style={{ backgroundColor: '#fff' }}>
        <div className="max-w-7xl mx-auto">
          <Reveal className="text-center mb-16">
            <Heading>
              Your Brand Could<br />
              <span style={{ color: COLORS.AUTUMN, fontStyle: 'italic' }}>Look Like This</span>
            </Heading>
            <p className="text-lg mt-5 max-w-2xl mx-auto" style={{ color: COLORS.TEXT_MUTED, lineHeight: 1.7 }}>
              Choose from 20+ professionally designed templates — each one built specifically for fashion brands
            </p>
          </Reveal>

          <StaggerWrap className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-7 mb-12" stagger={0.12}>
            {[
              { img: tplEthnic, name: 'Heritage', style: 'Indian & Elegant' },
              { img: tplMinimal, name: 'Minimal', style: 'Clean & Modern' },
              { img: tplStreet, name: 'Streetwear', style: 'Bold & Sporty' },
              { img: tplLuxury, name: 'Luxury', style: 'Premium & Refined' },
              { img: tplTrendy, name: 'Motion', style: 'Trendy & Gen-Z' },
              { img: tplVibrant, name: 'Vibrant', style: 'Colorful & Fun' },
            ].map((tmpl, i) => (
              <StaggerChild key={i} className={i >= 3 ? 'hidden lg:block' : ''} direction={i % 2 === 0 ? 'up' : 'scale'}>
                <TiltCard className="group relative rounded-2xl overflow-hidden cursor-pointer h-full" style={{ backgroundColor: COLORS.LINEN }}>
                  <div className="aspect-[4/3] overflow-hidden">
                    <motion.img
                      src={tmpl.img}
                      alt={tmpl.name}
                      className="w-full h-full object-cover object-top"
                      whileHover={{ scale: 1.08 }}
                      transition={{ duration: 0.7, ease: EASE }}
                    />
                  </div>
                  <div className="p-5">
                    <div className="text-[11px] font-semibold mb-1.5" style={{ color: COLORS.AUTUMN, letterSpacing: '0.08em' }}>
                      {tmpl.style}
                    </div>
                    <h3 className="text-base font-bold" style={{ color: COLORS.TEXT_DARK }}>{tmpl.name}</h3>
                  </div>
                  <motion.div
                    className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent flex items-center justify-center"
                    initial={{ opacity: 0 }}
                    whileHover={{ opacity: 1 }}
                    transition={{ duration: 0.4 }}
                  >
                    <motion.div
                      className="bg-white/95 backdrop-blur-sm rounded-full px-5 py-2.5 text-[13px] font-bold flex items-center gap-2"
                      style={{ color: COLORS.TEXT_DARK }}
                      initial={{ y: 20, opacity: 0 }}
                      whileHover={{ y: 0, opacity: 1 }}
                      transition={{ duration: 0.3 }}
                    >
                      <Eye className="w-4 h-4" /> Preview
                    </motion.div>
                  </motion.div>
                </TiltCard>
              </StaggerChild>
            ))}
          </StaggerWrap>

          <Reveal className="text-center" direction="scale">
            <MagneticWrap>
              <Link to="/Register">
                <motion.button
                  whileHover={{ scale: 1.04, backgroundColor: `${COLORS.AUTUMN}08` }}
                  whileTap={{ scale: 0.97 }}
                  className="px-8 py-3.5 rounded-xl font-bold text-[14px] inline-flex items-center gap-2 transition-all duration-300"
                  style={{ color: COLORS.AUTUMN, border: `2px solid ${COLORS.AUTUMN}` }}
                >
                  Browse All 20+ Templates
                  <ArrowRight className="w-4 h-4" />
                </motion.button>
              </Link>
            </MagneticWrap>
          </Reveal>
        </div>
      </section>

      {/* ══════════════════════════════════════════════
          FEATURES — alternating reveal directions
      ══════════════════════════════════════════════ */}
      <section className="py-24 sm:py-32 px-4 sm:px-6 lg:px-8" style={{ backgroundColor: COLORS.LINEN }}>
        <div className="max-w-6xl mx-auto">
          <Reveal className="text-center mb-16">
            <Heading>Everything You Need</Heading>
            <p className="text-[2.25rem] sm:text-[3rem] lg:text-[3.75rem] mt-1"
              style={{ fontFamily: SERIF, fontWeight: 400, lineHeight: 1.1, letterSpacing: '-0.02em', color: COLORS.TEXT_MUTED, opacity: 0.35 }}>
              Built Right In
            </p>
          </Reveal>

          <StaggerWrap className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5" stagger={0.07}>
            {[
              { icon: Globe, title: 'Your Own Branded URL', desc: 'yourbrand.vasstr.com — professional and memorable' },
              { icon: Smartphone, title: 'Mobile Optimized', desc: 'Looks perfect on phones, tablets, and desktops' },
              { icon: CreditCard, title: 'Razorpay Payments', desc: 'Accept payments securely — UPI, cards, netbanking' },
              { icon: Package, title: 'Product Management', desc: 'Upload unlimited products with photos, sizes, prices' },
              { icon: BarChart3, title: 'Analytics Dashboard', desc: 'Track orders, revenue, and customer insights' },
              { icon: Users, title: 'Customer Management', desc: 'See all your customers, their orders, and history' },
              { icon: Gift, title: 'Coupon System', desc: 'Create discount codes to boost your sales' },
              { icon: Instagram, title: 'SEO & Blog Tools', desc: 'Get discovered on Google with built-in SEO tools' },
              { icon: MessageCircle, title: 'WhatsApp Integration', desc: 'Customers can reach you directly via WhatsApp' },
            ].map((f, i) => (
              <StaggerChild key={i} direction={['left', 'up', 'right'][i % 3]}>
                <motion.div
                  whileHover={{ y: -6, boxShadow: '0 20px 50px -12px rgba(0,0,0,0.1)', scale: 1.02 }}
                  className="bg-white rounded-2xl p-6 h-full transition-all duration-500 cursor-default"
                  style={{ border: '1px solid rgba(0,0,0,0.04)' }}
                >
                  <motion.div
                    className="w-11 h-11 rounded-xl flex items-center justify-center mb-4"
                    style={{ backgroundColor: `${COLORS.AUTUMN}10` }}
                    whileHover={{ rotate: 8, scale: 1.15 }}
                    transition={{ type: 'spring', stiffness: 300, damping: 15 }}
                  >
                    <f.icon className="w-5 h-5" style={{ color: COLORS.AUTUMN }} />
                  </motion.div>
                  <h3 className="text-[16px] font-bold mb-2" style={{ color: COLORS.TEXT_DARK, letterSpacing: '-0.01em' }}>
                    {f.title}
                  </h3>
                  <p className="text-[14px]" style={{ color: COLORS.TEXT_MUTED, lineHeight: 1.65 }}>{f.desc}</p>
                </motion.div>
              </StaggerChild>
            ))}
          </StaggerWrap>
        </div>
      </section>

      {/* ══════════════════════════════════════════════
          PRICING — with animated counters
      ══════════════════════════════════════════════ */}
      <section className="py-24 sm:py-32 px-4 sm:px-6 lg:px-8" style={{ backgroundColor: '#fff' }}>
        <div className="max-w-4xl mx-auto text-center">
          <Reveal>
            <Heading>Simple, Honest Pricing</Heading>
            <p className="text-lg mt-5 mb-14 max-w-2xl mx-auto" style={{ color: COLORS.TEXT_MUTED, lineHeight: 1.7 }}>
              Start free. Upgrade when you're ready. No hidden fees, no surprises.
            </p>
          </Reveal>

          <StaggerWrap className="grid sm:grid-cols-3 gap-5 max-w-3xl mx-auto mb-12" stagger={0.15}>
            {[
              { label: 'Free Trial', price: 0, period: '30 days', desc: 'Full access, no card needed' },
              { label: 'Starter', price: 2250, period: '/month', desc: '3-month plan at ₹6,750' },
              { label: 'Pro', price: 2000, period: '/month', desc: '12-month plan at ₹24,000', featured: true },
            ].map((plan, i) => (
              <StaggerChild key={i} direction="scale">
                <motion.div
                  whileHover={{
                    y: -8,
                    boxShadow: plan.featured
                      ? `0 30px 60px -15px ${COLORS.MIDNIGHT}50`
                      : '0 25px 50px -12px rgba(0,0,0,0.1)'
                  }}
                  className={`rounded-2xl p-6 text-center h-full transition-all duration-500 ${plan.featured ? 'relative' : ''}`}
                  style={plan.featured
                    ? { backgroundColor: COLORS.MIDNIGHT, color: '#fff', boxShadow: `0 16px 40px -8px ${COLORS.MIDNIGHT}30` }
                    : { backgroundColor: '#fff', border: `1px solid ${COLORS.LINEN}` }
                  }
                >
                  {plan.featured && (
                    <motion.div
                      className="text-[10px] font-bold uppercase mb-3"
                      style={{ letterSpacing: '0.18em', color: COLORS.AUTUMN_LIGHT }}
                      animate={{ opacity: [0.7, 1, 0.7] }}
                      transition={{ duration: 2, repeat: Infinity, ease: 'easeInOut' }}
                    >
                      Most Popular
                    </motion.div>
                  )}
                  <div className="text-[11px] font-bold uppercase mb-2"
                    style={{ letterSpacing: '0.15em', color: plan.featured ? 'rgba(255,255,255,0.6)' : COLORS.AUTUMN }}>
                    {plan.label}
                  </div>
                  <div className="text-[2.25rem] font-extrabold mb-1" style={{ letterSpacing: '-0.02em' }}>
                    {plan.price === 0 ? '₹0' : <CountUp end={plan.price} prefix="₹" />}
                  </div>
                  <div className="text-[13px] mb-3" style={{ color: plan.featured ? 'rgba(255,255,255,0.4)' : COLORS.TEXT_MUTED }}>
                    {plan.period}
                  </div>
                  <div className="text-[13px]" style={{ color: plan.featured ? 'rgba(255,255,255,0.5)' : COLORS.TEXT_MUTED }}>
                    {plan.desc}
                  </div>
                </motion.div>
              </StaggerChild>
            ))}
          </StaggerWrap>

          <Reveal>
            <Link to="/subscription">
              <motion.button
                whileHover={{ scale: 1.03, x: 3 }}
                whileTap={{ scale: 0.97 }}
                className="text-[14px] font-semibold hover:underline underline-offset-4 inline-flex items-center gap-1.5 transition-all"
                style={{ color: COLORS.AUTUMN }}
              >
                View full pricing & features <ArrowRight className="w-4 h-4" />
              </motion.button>
            </Link>
          </Reveal>
        </div>
      </section>

      {/* ══════════════════════════════════════════════
          LIVE STORES — MIDNIGHT with parallax feel
      ══════════════════════════════════════════════ */}
      <section className="relative py-24 sm:py-32 px-4 sm:px-6 lg:px-8 overflow-hidden" style={{ backgroundColor: COLORS.LINEN }}>
        <div className="relative max-w-5xl mx-auto text-center">
          <Reveal>
            <Heading>Real Stores. Real Designers.</Heading>
            <p className="text-lg mt-5 mb-14 max-w-2xl mx-auto" style={{ color: COLORS.TEXT_MUTED, lineHeight: 1.7 }}>
              Fashion brands across India are already selling on Vasstr. See their live stores.
            </p>
          </Reveal>

          <StaggerWrap className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 mb-10" stagger={0.18}>
            {[
              { name: 'Meraki', url: 'meraki.vasstr.com', img: tplMinimal },
              { name: 'Niha Creations', url: 'nihacreations.vasstr.com', img: tplEthnic },
              { name: 'Snithi Fashion', url: 'snithi.vasstr.com', img: tplVibrant },
            ].map((store, i) => (
              <StaggerChild key={i}>
                <motion.div
                  whileHover={{ y: -8, boxShadow: '0 30px 60px -15px rgba(0,0,0,0.12)' }}
                  className="group rounded-2xl overflow-hidden transition-all duration-500 cursor-pointer bg-white"
                  style={{ border: `1px solid ${COLORS.AUTUMN}10` }}
                >
                  <div className="aspect-[16/10] overflow-hidden">
                    <motion.img
                      src={store.img}
                      alt={store.name}
                      className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-700"
                      whileHover={{ scale: 1.08 }}
                      transition={{ duration: 0.7, ease: EASE }}
                    />
                  </div>
                  <div className="p-5 flex items-center justify-between">
                    <div className="text-left">
                      <div className="text-[15px] font-bold" style={{ color: COLORS.TEXT_DARK }}>{store.name}</div>
                      <div className="text-[13px] font-medium" style={{ color: COLORS.TEXT_MUTED }}>{store.url}</div>
                    </div>
                    <motion.div
                      className="w-2.5 h-2.5 rounded-full"
                      style={{ backgroundColor: COLORS.AUTUMN }}
                      animate={{ scale: [1, 1.4, 1], opacity: [0.8, 1, 0.8], boxShadow: [`0 0 0px ${COLORS.AUTUMN}00`, `0 0 12px ${COLORS.AUTUMN}80`, `0 0 0px ${COLORS.AUTUMN}00`] }}
                      transition={{ duration: 2, repeat: Infinity, ease: 'easeInOut' }}
                    />
                  </div>
                </motion.div>
              </StaggerChild>
            ))}
          </StaggerWrap>

          <Reveal>
            <p className="text-[14px] font-medium" style={{ color: COLORS.TEXT_MUTED }}>
              Your store could be next
            </p>
          </Reveal>
        </div>
      </section>

      {/* ══════════════════════════════════════════════
          FINAL CTA — animated gradient background
      ══════════════════════════════════════════════ */}
      <section className="relative py-28 sm:py-36 px-4 sm:px-6 lg:px-8 overflow-hidden" style={{ backgroundColor: COLORS.LINEN }}>
        {/* Animated orbs */}
        <motion.div
          className="absolute w-[500px] h-[500px] rounded-full blur-[130px]"
          style={{ backgroundColor: `${COLORS.AUTUMN}08`, top: '-15%', right: '-5%' }}
          animate={{ x: [0, 50, 0], y: [0, -30, 0], opacity: [0.4, 0.7, 0.4] }}
          transition={{ duration: 12, repeat: Infinity, ease: 'easeInOut' }}
        />
        <motion.div
          className="absolute w-[400px] h-[400px] rounded-full blur-[100px]"
          style={{ backgroundColor: `${COLORS.MIDNIGHT}08`, bottom: '-10%', left: '-5%' }}
          animate={{ x: [0, -40, 0], y: [0, 30, 0] }}
          transition={{ duration: 15, repeat: Infinity, ease: 'easeInOut' }}
        />

        <Reveal className="relative max-w-3xl mx-auto text-center">
          <Heading>
            Ready to Build<br />
            <span style={{ color: COLORS.AUTUMN, fontStyle: 'italic' }}>Your Brand's Future?</span>
          </Heading>
          <p className="text-lg mt-5 mb-10 max-w-xl mx-auto" style={{ color: COLORS.TEXT_MUTED, lineHeight: 1.7 }}>
            Join fashion designers across India who are growing their business with their own professional website.
          </p>

          <MagneticWrap>
            <Link to="/Register">
              <motion.button
                whileHover={{ scale: 1.05, boxShadow: `0 35px 70px -15px ${COLORS.AUTUMN}55` }}
                whileTap={{ scale: 0.97 }}
                className="group relative px-12 py-5 rounded-2xl font-bold text-white text-lg inline-flex items-center gap-2.5 overflow-hidden"
                style={{ backgroundColor: COLORS.AUTUMN, boxShadow: `0 20px 50px -10px ${COLORS.AUTUMN}40` }}
              >
                <motion.div
                  className="absolute inset-0 rounded-2xl"
                  style={{ background: `radial-gradient(circle at center, ${COLORS.AUTUMN_LIGHT}40 0%, transparent 70%)` }}
                  animate={{ opacity: [0, 0.5, 0] }}
                  transition={{ duration: 3, repeat: Infinity, ease: 'easeInOut' }}
                />
                <span className="relative z-10 flex items-center gap-2.5">
                  Get Your Free Website Now
                  <ArrowRight className="w-5 h-5 group-hover:translate-x-1.5 transition-transform duration-300" />
                </span>
              </motion.button>
            </Link>
          </MagneticWrap>

          <div className="mt-8 flex flex-wrap items-center justify-center gap-4">
            {[
              { text: 'Free 30-day trial' },
              { text: 'No credit card' },
              { text: 'Live in 5 minutes' },
            ].map((item, i) => (
              <motion.span
                key={i}
                className="flex items-center gap-1.5 text-[13px] font-medium"
                style={{ color: COLORS.TEXT_MUTED }}
                initial={{ opacity: 0, y: 10 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.3 + i * 0.12, duration: 0.5, ease: EASE }}
              >
                <Check className="w-3.5 h-3.5" style={{ color: COLORS.AUTUMN }} />
                {item.text}
                {i < 2 && <span className="ml-3" style={{ color: '#ddd' }}>·</span>}
              </motion.span>
            ))}
          </div>
        </Reveal>
      </section>

      {/* Footer */}
      <VFooter />

      {/* Video Modal */}
      <AnimatePresence>
        {showVideoModal && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4"
            style={{ backgroundColor: 'rgba(0,0,0,0.7)', backdropFilter: 'blur(10px)' }}
            onClick={() => setShowVideoModal(false)}
          >
            <motion.div
              initial={{ scale: 0.85, opacity: 0, y: 30 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.85, opacity: 0, y: 30 }}
              transition={{ duration: 0.35, ease: EASE }}
              className="bg-white rounded-2xl p-7 max-w-2xl w-full"
              style={{ boxShadow: '0 40px 80px -20px rgba(0,0,0,0.3)' }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex justify-between items-center mb-5">
                <h3 className="text-lg font-bold" style={{ fontFamily: SERIF, color: COLORS.TEXT_DARK }}>How Vasstr Works</h3>
                <motion.button
                  whileHover={{ scale: 1.1, rotate: 90, backgroundColor: COLORS.LINEN }}
                  whileTap={{ scale: 0.9 }}
                  onClick={() => setShowVideoModal(false)}
                  className="p-2 rounded-lg transition-colors"
                >
                  <X className="w-5 h-5" style={{ color: COLORS.TEXT_MUTED }} />
                </motion.button>
              </div>
              <div className="rounded-xl aspect-video flex items-center justify-center" style={{ backgroundColor: COLORS.LINEN }}>
                <div className="text-center">
                  <motion.div
                    animate={{ scale: [1, 1.1, 1] }}
                    transition={{ duration: 2, repeat: Infinity, ease: 'easeInOut' }}
                  >
                    <Play className="w-16 h-16 mx-auto mb-3" style={{ color: COLORS.AUTUMN }} />
                  </motion.div>
                  <p className="text-[14px]" style={{ color: COLORS.TEXT_MUTED }}>Demo video coming soon</p>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default LandingPage;
